import { BridgeComponent, BridgeElement } from "@hotwired/hotwire-native-bridge";

/**
 * SingleActionComponent - Adds a sticky bottom bar with one action button
 *
 * ## Usage
 * Add the `bridge--single-action` controller to any element:
 *
 * ```erb
 * <div data-controller="bridge--single-action"
 *      data-bridge--single-action-title-value="Add to routine"
 *      data-bridge--single-action-icon-value="plus.circle.fill"
 *      data-bridge--single-action-color-value="#312e81"
 *      data-bridge--single-action-text-color-value="#ffffff"
 *      class="hidden">
 *   <a href="/add" data-bridge--single-action-target="action"></a>
 * </div>
 * ```
 *
 * ## Stimulus Values
 * - `title` (String): Button text label
 * - `icon` (String): SF Symbol name for the button
 * - `color` (String): Hex color for the button background
 * - `textColor` (String, optional): Hex color for the button text/icon
 * - `loading` (Boolean, optional): Shows native progress and disables taps
 * - `disabled` (Boolean, optional): Disables the native action
 * - `minimumWidth` (Number, optional): Uses intrinsic width with this minimum instead of full width
 *
 * ## Targets
 * - `action`: Element to click when the button is tapped
 *
 * ## Behavior
 * - Displays one full-width button at the bottom of the screen
 * - Uses glassEffect on iOS 26+
 * - When tapped, triggers the action target element's click handler
 */
export default class extends BridgeComponent {
  static component = "single-action";
  static targets = ["action"];
  static values = {
    title: String,
    icon: String,
    color: String,
    textColor: String,
    loading: Boolean,
    disabled: Boolean,
    minimumWidth: Number,
  };

  connect() {
    super.connect();
    this.bridgeConnected = true;
    this.#notifyBridgeOfConnect();
  }

  disconnect() {
    this.bridgeConnected = false;
    super.disconnect();
    this.send("disconnect", {}, () => { });
  }

  titleValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  iconValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  colorValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  textColorValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  loadingValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  disabledValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  minimumWidthValueChanged() {
    this.#scheduleBridgeUpdate();
  }

  #scheduleBridgeUpdate() {
    if (!this.bridgeConnected || this.bridgeUpdateScheduled) return;

    this.bridgeUpdateScheduled = true;
    queueMicrotask(() => {
      this.bridgeUpdateScheduled = false;

      if (this.bridgeConnected) this.#notifyBridgeOfConnect();
    });
  }

  #notifyBridgeOfConnect() {
    const data = {
      title: this.hasTitleValue ? this.titleValue : "Action",
      icon: this.hasIconValue ? this.iconValue : "star.fill",
      color: this.hasColorValue ? this.colorValue : "#312e81",
      textColor: this.hasTextColorValue ? this.textColorValue : null,
      loading: this.hasLoadingValue && this.loadingValue,
      disabled: this.hasDisabledValue && this.disabledValue,
      minimumWidth: this.hasMinimumWidthValue ? this.minimumWidthValue : null,
    };

    this.send("connect", data, () => {
      // Callback when native button is tapped
      if (!this.loadingValue && !this.disabledValue && this.hasActionTarget) {
        new BridgeElement(this.actionTarget).click();
      }
    });
  }
}
